import React, { useState } from 'react';
import { InventoryDocument, InventoryUpdateVo, CoordinatorAmountVo, ImageInfo, FaqModel, MyLocationVo2, InstagramReel } from '../../types';
import { Plus, Trash2, ArrowUp, ArrowDown, GripVertical, Copy } from 'lucide-react';
import { apiService } from '../../services/api';
import toast from 'react-hot-toast';

interface UpdateInventoryFormProps {
  inventory: InventoryDocument;
  onSubmit: (updates: InventoryUpdateVo) => void;
  onCancel: () => void;
  loading: boolean;
}

export const UpdateInventoryForm: React.FC<UpdateInventoryFormProps> = ({
  inventory,
  onSubmit,
  onCancel,
  loading,
}) => {
  // Transform the response data to match form structure
  // Map imageList from API format (url) to ImageInfo format (endPoint)
  const transformedImageList: ImageInfo[] = (inventory.imageList || []).map((img: any) => ({
    endPoint: img.url || img.endPoint || '',
    caption: img.caption || '',
    date: img.date || '',
    tag: img.tag || '',
    cloudName: img.cloudName || '',
    surl: img.surl || '',
    murl: img.murl || '',
    lurl: img.lurl || '',
  }));

  // Map FAQs from API format (q, a) to FaqModel format (question, answer)
  const transformedFaqs: FaqModel[] = (inventory.faqs || []).map((faq: any) => ({
    question: faq.q || faq.question || '',
    answer: faq.a || faq.answer || '',
  }));

  // Handle seo_meta_description (with underscore) from API
  const seoMetaDescription = (inventory as any).seo_meta_description || (inventory as any).seoMetaDescription || '';

  const [formData, setFormData] = useState<InventoryUpdateVo>({
    title: inventory.title || '',
    slug: (inventory as any).slug || inventory.seoPath || '',
    seoMetaDescription: seoMetaDescription,
    priceTag: (inventory as any).priceTag || '',
    about: inventory.about || '',
    duration: inventory.duration || '',
    durationInMillis: inventory.durationInMillis || 0,
    type: inventory.type || '',
    variant: inventory.variant || 'GIT',
    genderAllowed: inventory.genderAllowed || 0,
    smokingAllowed: inventory.smokingAllowed || false,
    alcoholAllowed: inventory.alcoholAllowed || false,
    special: (inventory as any).special || false,
    transportIncluded: inventory.transportIncluded || false,
    vehicle: inventory.vehicle || '',
    acVehicle: inventory.acVehicle || false,
    vehicleNote: inventory.vehicleNote || '',
    stayIncluded: inventory.stayIncluded || false,
    acStay: inventory.acStay || false,
    stayType: inventory.stayType || '',
    stayNote: inventory.stayNote || '',
    foodIncluded: inventory.foodIncluded || false,
    foodType: inventory.foodType || 0,
    foodNote: inventory.foodNote || '',
    inclusion: inventory.inclusion || '',
    exclusion: inventory.exclusion || '',
    thingsToCarry: inventory.thingsToCarry || '',
    itinerary: inventory.itinerary || '',
    rules: inventory.rules || '',
    cancellationPolicy: inventory.cancellationPolicy || '',
    conditions: inventory.conditions || '',
    usp: inventory.usp || '',
    smsTitle: inventory.smsTitle || '',
    appTitle: inventory.appTitle || '',
    appSubTitle: inventory.appSubTitle || '',
    whatsAppItinerary: inventory.whatsAppItinerary || '',
    convenienceFee: inventory.convenienceFee || 5.0,
    gst: inventory.gst || 5.0,
    convenienceFeeText: inventory.convenienceFeeText || 'Convenience fee (5%)',
    cost: inventory.cost || 0,
    image: inventory.image || '',
    imageList: transformedImageList,
    rawSearchIndex: inventory.rawSearchIndex || '',
    rating: inventory.rating || 0,
    ratingCount: inventory.ratingCount || 0,
    typeTag: inventory.typeTag || '',
    hide: inventory.hide || false,
    faqs: transformedFaqs,
    headAmount: inventory.headAmount || [],
    mainAmount: inventory.mainAmount || [],
    traineeAmount: inventory.traineeAmount || [],
    pickUpLocations: (inventory.pickUpLocations || []).map((loc, index) => ({
      ...loc,
      seqNo: loc.seqNo !== undefined ? loc.seqNo : index,
    })),
    instagramReels: (inventory as any).instagramReels || [],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.title?.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (formData.whatsAppItinerary && formData.whatsAppItinerary.length > 1023) {
      newErrors.whatsAppItinerary = 'WhatsApp Itinerary cannot be more than 1024 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      // Transform formData to match API expectations
      const updateData: any = { ...formData };
      
      // Transform seoMetaDescription to seo_meta_description for API
      if (updateData.seoMetaDescription !== undefined) {
        updateData.seo_meta_description = updateData.seoMetaDescription;
        delete updateData.seoMetaDescription;
      }
      
      // Transform imageList items from endPoint to url for API
      if (updateData.imageList) {
        updateData.imageList = updateData.imageList.map((img: ImageInfo) => ({
          url: img.endPoint,
          caption: img.caption || '',
          date: img.date || '',
          tag: img.tag || '',
          cloudName: img.cloudName || '',
          surl: img.surl || '',
          murl: img.murl || '',
          lurl: img.lurl || '',
        }));
      }
      
      // Transform FAQs from question/answer to q/a for API
      if (updateData.faqs) {
        updateData.faqs = updateData.faqs.map((faq: FaqModel) => ({
          q: faq.question,
          a: faq.answer,
        }));
      }
      
      onSubmit(updateData);
    }
  };

  const handleInputChange = (field: keyof InventoryUpdateVo, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  // Coordinator amount management functions
  const addCoordinatorAmount = (type: 'headAmount' | 'mainAmount' | 'traineeAmount') => {
    const newAmount: CoordinatorAmountVo = {
      title: '',
      amount: 0,
      editable: true,
    };
    
    setFormData((prev) => ({
      ...prev,
      [type]: [...(prev[type] || []), newAmount],
    }));
  };

  const updateCoordinatorAmount = (
    type: 'headAmount' | 'mainAmount' | 'traineeAmount',
    index: number,
    field: keyof CoordinatorAmountVo,
    value: any
  ) => {
    setFormData((prev) => {
      const updated = [...(prev[type] || [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, [type]: updated };
    });
  };

  const removeCoordinatorAmount = (type: 'headAmount' | 'mainAmount' | 'traineeAmount', index: number) => {
    setFormData((prev) => {
      const updated = [...(prev[type] || [])];
      updated.splice(index, 1);
      return { ...prev, [type]: updated };
    });
  };

  // FAQ management functions
  const addFAQ = () => {
    const newFAQ: FaqModel = {
      question: '',
      answer: '',
    };
    setFormData((prev) => ({
      ...prev,
      faqs: [...(prev.faqs || []), newFAQ],
    }));
  };

  const updateFAQ = (index: number, field: keyof FaqModel, value: string) => {
    setFormData((prev) => {
      const updated = [...(prev.faqs || [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, faqs: updated };
    });
  };

  const removeFAQ = (index: number) => {
    setFormData((prev) => {
      const updated = [...(prev.faqs || [])];
      updated.splice(index, 1);
      return { ...prev, faqs: updated };
    });
  };

  // PickUp Locations management functions
  const addPickUpLocation = () => {
    const newLocation: MyLocationVo2 = {
      id: `pickup-${Date.now()}`,
      name: '',
      url: '',
      displayText: '',
      seqNo: (formData.pickUpLocations?.length || 0),
    };
    setFormData((prev) => ({
      ...prev,
      pickUpLocations: [...(prev.pickUpLocations || []), newLocation],
    }));
  };

  const updatePickUpLocation = (index: number, field: keyof MyLocationVo2, value: any) => {
    setFormData((prev) => {
      const updated = [...(prev.pickUpLocations || [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, pickUpLocations: updated };
    });
  };

  const removePickUpLocation = (index: number) => {
    setFormData((prev) => {
      const updated = [...(prev.pickUpLocations || [])];
      updated.splice(index, 1);
      // Update seqNo for remaining items
      const reordered = updated.map((loc, idx) => ({
        ...loc,
        seqNo: idx,
      }));
      return { ...prev, pickUpLocations: reordered };
    });
  };

  const movePickUpLocation = (sortedIndex: number, direction: 'up' | 'down') => {
    setFormData((prev) => {
      const locations = [...(prev.pickUpLocations || [])];
      const sorted = [...locations].sort((a, b) => (a.seqNo || 0) - (b.seqNo || 0));
      
      if (direction === 'up' && sortedIndex > 0) {
        // Swap seqNo values
        const tempSeqNo = sorted[sortedIndex].seqNo || sortedIndex;
        sorted[sortedIndex].seqNo = sorted[sortedIndex - 1].seqNo ?? sortedIndex - 1;
        sorted[sortedIndex - 1].seqNo = tempSeqNo;
      } else if (direction === 'down' && sortedIndex < sorted.length - 1) {
        // Swap seqNo values
        const tempSeqNo = sorted[sortedIndex].seqNo || sortedIndex;
        sorted[sortedIndex].seqNo = sorted[sortedIndex + 1].seqNo ?? sortedIndex + 1;
        sorted[sortedIndex + 1].seqNo = tempSeqNo;
      }
      
      // Update the original array with new seqNo values
      sorted.forEach(sortedLoc => {
        const originalIndex = locations.findIndex(loc => loc.id === sortedLoc.id);
        if (originalIndex !== -1) {
          locations[originalIndex].seqNo = sortedLoc.seqNo;
        }
      });
      
      return { ...prev, pickUpLocations: locations };
    });
  };

  // Instagram Reels management functions
  const addInstagramReel = () => {
    const newReel: InstagramReel = {
      title: '',
      url: '',
    };
    setFormData((prev) => ({
      ...prev,
      instagramReels: [...(prev.instagramReels || []), newReel],
    }));
  };

  const updateInstagramReel = (index: number, field: keyof InstagramReel, value: string) => {
    setFormData((prev) => {
      const updated = [...(prev.instagramReels || [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, instagramReels: updated };
    });
  };

  const removeInstagramReel = (index: number) => {
    setFormData((prev) => {
      const updated = [...(prev.instagramReels || [])];
      updated.splice(index, 1);
      return { ...prev, instagramReels: updated };
    });
  };


  return (
    <div className="max-w-4xl mx-auto relative">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 pb-20">
          <div className="mb-6">
          <div className="flex items-center mb-4">
            <button
              onClick={onCancel}
              className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mr-4"
              disabled={loading}
            >
              <svg className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Inventory List
            </button>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Edit Inventory</h2>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Update inventory details and settings
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600 dark:text-gray-400 font-mono">
                ID: {inventory.id}
              </span>
              <button
                type="button"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(inventory.id);
                    toast.success('Inventory ID copied to clipboard!');
                  } catch (error) {
                    toast.error('Failed to copy inventory ID');
                  }
                }}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                disabled={loading}
                title="Copy Inventory ID"
              >
                <Copy className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Basic Information</h3>
            
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Title *
              </label>
              <input
                type="text"
                id="title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                  errors.title ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter inventory title"
                disabled={loading}
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-600">{errors.title}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="slug" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Slug
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    id="slug"
                    value={formData.slug}
                    onChange={(e) => handleInputChange('slug', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed"
                    placeholder="e.g., gokarna-2-days-trip-from-bangalore"
                    disabled={loading || Boolean(formData.slug && formData.slug.trim() !== '')}
                  />
                  {formData.slug && formData.slug.trim() !== '' && (
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(formData.slug || '');
                          toast.success('Slug copied to clipboard!');
                        } catch (error) {
                          toast.error('Failed to copy slug');
                        }
                      }}
                      className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      disabled={loading}
                      title="Copy Slug"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {formData.slug && formData.slug.trim() !== '' && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Slug cannot be changed once set
                  </p>
                )}
              </div>

              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.special || false}
                    onChange={(e) => handleInputChange('special', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    disabled={loading}
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Special</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.hide || false}
                    onChange={(e) => handleInputChange('hide', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    disabled={loading}
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Hide</span>
                </label>
              </div>
            </div>

            <div>
              <label htmlFor="seoMetaDescription" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                SEO Meta Description
              </label>
              <textarea
                id="seoMetaDescription"
                value={formData.seoMetaDescription || ''}
                onChange={(e) => handleInputChange('seoMetaDescription', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="SEO meta description for search engines"
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="about" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                About
              </label>
              <textarea
                id="about"
                value={formData.about}
                onChange={(e) => handleInputChange('about', e.target.value)}
                rows={8}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Describe the inventory"
                disabled={loading}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label htmlFor="duration" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Duration
                </label>
                <input
                  type="text"
                  id="duration"
                  value={formData.duration}
                  onChange={(e) => handleInputChange('duration', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="e.g., 2 Days 1 Night"
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="variant" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Variant
                </label>
                <select
                  id="variant"
                  value={formData.variant}
                  onChange={(e) => handleInputChange('variant', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  disabled={loading}
                >
                  <option value="GIT">GIT (Group Inclusive Tour)</option>
                  <option value="FIT">FIT (Free Independent Traveler)</option>
                </select>
              </div>

              <div>
                <label htmlFor="cost" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Base Cost (₹)
                </label>
                <input
                  type="number"
                  id="cost"
                  value={formData.cost}
                  onChange={(e) => handleInputChange('cost', parseInt(e.target.value) || 0)}
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Base price"
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          {/* Trip Details */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Trip Details</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label htmlFor="genderAllowed" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Gender Allowed
                </label>
                <select
                  id="genderAllowed"
                  value={formData.genderAllowed}
                  onChange={(e) => handleInputChange('genderAllowed', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  disabled={loading}
                >
                  <option value={0}>All</option>
                  <option value={1}>Male Only</option>
                  <option value={2}>Female Only</option>
                </select>
              </div>

              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.smokingAllowed}
                    onChange={(e) => handleInputChange('smokingAllowed', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    disabled={loading}
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Smoking Allowed</span>
                </label>
              </div>

              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.alcoholAllowed}
                    onChange={(e) => handleInputChange('alcoholAllowed', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    disabled={loading}
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Alcohol Allowed</span>
                </label>
              </div>
            </div>
          </div>

          {/* PickUp Locations */}
          {formData.variant !== 'FIT' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">PickUp Locations</h3>
              <button
                type="button"
                onClick={addPickUpLocation}
                className="flex items-center px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                disabled={loading}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add PickUp Location
              </button>
            </div>

            {formData.pickUpLocations && formData.pickUpLocations.length > 0 && (
              <div className="space-y-3">
                {[...formData.pickUpLocations]
                  .sort((a, b) => (a.seqNo || 0) - (b.seqNo || 0))
                  .map((location, index) => (
                      <div key={location.id} className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <GripVertical className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              PickUp Location {index + 1}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => movePickUpLocation(index, 'up')}
                              disabled={loading || index === 0}
                              className="p-1 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Move up"
                            >
                              <ArrowUp className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => movePickUpLocation(index, 'down')}
                              disabled={loading || index === formData.pickUpLocations!.length - 1}
                              className="p-1 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Move down"
                            >
                              <ArrowDown className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const sorted = [...formData.pickUpLocations!].sort((a, b) => (a.seqNo || 0) - (b.seqNo || 0));
                                const locationToRemove = sorted[index];
                                const actualIndex = formData.pickUpLocations!.findIndex(loc => loc.id === locationToRemove.id);
                                removePickUpLocation(actualIndex);
                              }}
                              className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                              disabled={loading}
                              title="Remove location"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Location Name *
                            </label>
                            <input
                              type="text"
                              value={location.name}
                              onChange={(e) => {
                                const actualIndex = formData.pickUpLocations!.findIndex(loc => loc.id === location.id);
                                updatePickUpLocation(actualIndex, 'name', e.target.value);
                              }}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:text-white"
                              placeholder="e.g., MG Road, Koramangala"
                              disabled={loading}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Display Text *
                            </label>
                            <input
                              type="text"
                              value={location.displayText}
                              onChange={(e) => {
                                const actualIndex = formData.pickUpLocations!.findIndex(loc => loc.id === location.id);
                                updatePickUpLocation(actualIndex, 'displayText', e.target.value);
                              }}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:text-white"
                              placeholder="e.g., 6:00 AM, Near Metro Station"
                              disabled={loading}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Map URL / Location URL *
                            </label>
                            <input
                              type="text"
                              value={location.url}
                              onChange={(e) => {
                                const actualIndex = formData.pickUpLocations!.findIndex(loc => loc.id === location.id);
                                updatePickUpLocation(actualIndex, 'url', e.target.value);
                              }}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:text-white"
                              placeholder="e.g., https://maps.google.com/..."
                              disabled={loading}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
              </div>
            )}

            {(!formData.pickUpLocations || formData.pickUpLocations.length === 0) && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700">
                <p>No pickup locations added yet.</p>
                <p className="text-sm mt-1">Click "Add PickUp Location" to add one.</p>
              </div>
            )}
          </div>
          )}

          {/* Transport Details */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Transport Details</h3>
            
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.transportIncluded}
                  onChange={(e) => handleInputChange('transportIncluded', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  disabled={loading}
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Transport Included</span>
              </label>
            </div>

            {formData.transportIncluded && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="vehicle" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Vehicle Type
                  </label>
                  <input
                    type="text"
                    id="vehicle"
                    value={formData.vehicle}
                    onChange={(e) => handleInputChange('vehicle', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="e.g., AC Bus, Tempo Traveller"
                    disabled={loading}
                  />
                </div>

                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.acVehicle}
                      onChange={(e) => handleInputChange('acVehicle', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      disabled={loading}
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">AC Vehicle</span>
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Stay Details */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Stay Details</h3>
            
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.stayIncluded}
                  onChange={(e) => handleInputChange('stayIncluded', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  disabled={loading}
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Stay Included</span>
              </label>
            </div>

            {formData.stayIncluded && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="stayType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Stay Type
                  </label>
                  <input
                    type="text"
                    id="stayType"
                    value={formData.stayType}
                    onChange={(e) => handleInputChange('stayType', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="e.g., Hotel, Resort, Homestay"
                    disabled={loading}
                  />
                </div>

                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.acStay}
                      onChange={(e) => handleInputChange('acStay', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      disabled={loading}
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">AC Stay</span>
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Food Details */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Food Details</h3>
            
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.foodIncluded}
                  onChange={(e) => handleInputChange('foodIncluded', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  disabled={loading}
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Food Included</span>
              </label>
            </div>

            {formData.foodIncluded && (
              <div className="space-y-4">
                <div>
                  <label htmlFor="foodType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Food Type
                  </label>
                  <select
                    id="foodType"
                    value={formData.foodType}
                    onChange={(e) => handleInputChange('foodType', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    disabled={loading}
                  >
                    <option value={0}>Both Available (Veg/Non-Veg)</option>
                    <option value={1}>Veg Only</option>
                    <option value={2}>Non-Veg Only</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="foodNote" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Food Note
                  </label>
                  <textarea
                    id="foodNote"
                    value={formData.foodNote || ''}
                    onChange={(e) => handleInputChange('foodNote', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Additional notes about food (e.g., breakfast timing, special dietary options)"
                    disabled={loading}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Pricing & Fees */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Pricing & Fees</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label htmlFor="convenienceFee" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Convenience Fee (%)
                </label>
                <input
                  type="number"
                  id="convenienceFee"
                  value={formData.convenienceFee}
                  onChange={(e) => handleInputChange('convenienceFee', parseFloat(e.target.value) || 0)}
                  min="0"
                  max="100"
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="gst" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  GST (%)
                </label>
                <input
                  type="number"
                  id="gst"
                  value={formData.gst}
                  onChange={(e) => handleInputChange('gst', parseFloat(e.target.value) || 0)}
                  min="0"
                  max="100"
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="convenienceFeeText" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Convenience Fee Text
                </label>
                <input
                  type="text"
                  id="convenienceFeeText"
                  value={formData.convenienceFeeText}
                  onChange={(e) => handleInputChange('convenienceFeeText', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="e.g., Convenience fee (5%)"
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label htmlFor="priceTag" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Price Tag
              </label>
              <input
                type="text"
                id="priceTag"
                value={formData.priceTag || ''}
                onChange={(e) => handleInputChange('priceTag', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="e.g., Starting at ₹4,999"
                disabled={loading}
              />
            </div>
          </div>

          {/* Main Image */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Main Image</h3>
            
            <div>
              <label htmlFor="image" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Main Image URL
              </label>
              <input
                type="text"
                id="image"
                value={formData.image || ''}
                onChange={(e) => handleInputChange('image', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Main image URL"
                disabled={loading}
              />
              {formData.image && (
                <div className="mt-2">
                  <img
                    src={formData.image}
                    alt="Main image"
                    className="w-48 h-32 object-cover rounded-lg border border-gray-300"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* SEO & Metadata */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">SEO & Metadata</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label htmlFor="rawSearchIndex" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Raw Search Index
                </label>
                <input
                  type="text"
                  id="rawSearchIndex"
                  value={formData.rawSearchIndex || ''}
                  onChange={(e) => handleInputChange('rawSearchIndex', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="e.g., Gokarna,Two days trip, beaches"
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="rating" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Rating
                </label>
                <input
                  type="number"
                  id="rating"
                  value={formData.rating || 0}
                  onChange={(e) => handleInputChange('rating', parseFloat(e.target.value) || 0)}
                  min="0"
                  max="5"
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="ratingCount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Rating Count
                </label>
                <input
                  type="number"
                  id="ratingCount"
                  value={formData.ratingCount || 0}
                  onChange={(e) => handleInputChange('ratingCount', parseInt(e.target.value) || 0)}
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label htmlFor="typeTag" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Type Tag
              </label>
              <select
                id="typeTag"
                value={formData.typeTag || ''}
                onChange={(e) => handleInputChange('typeTag', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                disabled={loading}
              >
                <option value="">Select Type Tag</option>
                <option value="New">New</option>
                <option value="Popular">Popular</option>
                <option value="Featured">Featured</option>
                <option value="Trending">Trending</option>
              </select>
            </div>
          </div>

          {/* Additional Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Additional Information</h3>
            
            <div>
              <label htmlFor="inclusion" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Inclusions
              </label>
              <textarea
                id="inclusion"
                value={formData.inclusion}
                onChange={(e) => handleInputChange('inclusion', e.target.value)}
                rows={5}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="What's included in the trip"
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="exclusion" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Exclusions
              </label>
              <textarea
                id="exclusion"
                value={formData.exclusion}
                onChange={(e) => handleInputChange('exclusion', e.target.value)}
                rows={5}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="What's not included in the trip"
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="thingsToCarry" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Things to Carry
              </label>
              <textarea
                id="thingsToCarry"
                value={formData.thingsToCarry}
                onChange={(e) => handleInputChange('thingsToCarry', e.target.value)}
                rows={5}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="List of things travelers should carry"
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="itinerary" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Itinerary
              </label>
              <textarea
                id="itinerary"
                value={formData.itinerary}
                onChange={(e) => handleInputChange('itinerary', e.target.value)}
                rows={15}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Detailed day-by-day itinerary"
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="whatsAppItinerary" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                WhatsApp Itinerary
              </label>
              <textarea
                id="whatsAppItinerary"
                value={formData.whatsAppItinerary}
                onChange={(e) => handleInputChange('whatsAppItinerary', e.target.value)}
                rows={24}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white ${
                  errors.whatsAppItinerary ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="Detailed itinerary for WhatsApp sharing"
                disabled={loading}
              />
              {errors.whatsAppItinerary && (
                <p className="mt-1 text-sm text-red-600">{errors.whatsAppItinerary}</p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                {formData.whatsAppItinerary?.length || 0}/1024 characters
              </p>
            </div>
          </div>

          {/* Rules & Policies */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Rules & Policies</h3>
            
            <div>
              <label htmlFor="rules" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Rules
              </label>
              <textarea
                id="rules"
                value={formData.rules}
                onChange={(e) => handleInputChange('rules', e.target.value)}
                rows={10}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Rules and guidelines for the trip"
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="cancellationPolicy" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Cancellation Policy
              </label>
              <textarea
                id="cancellationPolicy"
                value={formData.cancellationPolicy}
                onChange={(e) => handleInputChange('cancellationPolicy', e.target.value)}
                rows={8}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Cancellation policy details"
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="conditions" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Conditions
              </label>
              <textarea
                id="conditions"
                value={formData.conditions}
                onChange={(e) => handleInputChange('conditions', e.target.value)}
                rows={12}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Terms and conditions"
                disabled={loading}
              />
            </div>
          </div>

          {/* Instagram Reels */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Instagram Reels</h3>
              <button
                type="button"
                onClick={addInstagramReel}
                className="flex items-center px-3 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                disabled={loading}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Instagram Reel
              </button>
            </div>

            {formData.instagramReels && formData.instagramReels.length > 0 && (
              <div className="space-y-4">
                {formData.instagramReels.map((reel, index) => (
                  <div key={index} className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700">
                    <div className="flex items-start justify-between mb-3">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Instagram Reel {index + 1}</span>
                      <button
                        type="button"
                        onClick={() => removeInstagramReel(index)}
                        className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                        disabled={loading}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Title
                        </label>
                        <input
                          type="text"
                          value={reel.title || ''}
                          onChange={(e) => updateInstagramReel(index, 'title', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:text-white"
                          placeholder="Enter reel title"
                          disabled={loading}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          URL *
                        </label>
                        <input
                          type="url"
                          value={reel.url || ''}
                          onChange={(e) => updateInstagramReel(index, 'url', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:text-white"
                          placeholder="https://www.instagram.com/reel/..."
                          disabled={loading}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {(!formData.instagramReels || formData.instagramReels.length === 0) && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700">
                <p>No Instagram reels added yet.</p>
                <p className="text-sm mt-1">Click "Add Instagram Reel" to add one.</p>
              </div>
            )}
          </div>

          {/* FAQs */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Frequently Asked Questions</h3>
              <button
                type="button"
                onClick={addFAQ}
                className="flex items-center px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                disabled={loading}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add FAQ
              </button>
            </div>

            {formData.faqs && formData.faqs.length > 0 && (
              <div className="space-y-4">
                {formData.faqs.map((faq, index) => (
                  <div key={index} className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700">
                    <div className="flex items-start justify-between mb-3">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">FAQ {index + 1}</span>
                      <button
                        type="button"
                        onClick={() => removeFAQ(index)}
                        className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                        disabled={loading}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Question
                        </label>
                        <input
                          type="text"
                          value={faq.question}
                          onChange={(e) => updateFAQ(index, 'question', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:text-white"
                          placeholder="Enter question"
                          disabled={loading}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Answer
                        </label>
                        <textarea
                          value={faq.answer}
                          onChange={(e) => updateFAQ(index, 'answer', e.target.value)}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:text-white"
                          placeholder="Enter answer"
                          disabled={loading}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>


          {/* Coordinator Amounts */}
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Coordinator Amounts</h3>
            
            {/* Head Amount */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-md font-medium text-gray-800 dark:text-gray-200">Head Coordinator Amounts</h4>
                <button
                  type="button"
                  onClick={() => addCoordinatorAmount('headAmount')}
                  className="flex items-center px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  disabled={loading}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Head Amount
                </button>
              </div>
              {formData.headAmount?.map((amount, index) => (
                <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <input
                    type="text"
                    value={amount.title}
                    onChange={(e) => updateCoordinatorAmount('headAmount', index, 'title', e.target.value)}
                    placeholder="Role title (e.g., Head Coordinator)"
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:text-white"
                    disabled={loading}
                  />
                  <input
                    type="number"
                    value={amount.amount}
                    onChange={(e) => updateCoordinatorAmount('headAmount', index, 'amount', parseInt(e.target.value) || 0)}
                    placeholder="Amount"
                    min="0"
                    className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:text-white"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => removeCoordinatorAmount('headAmount', index)}
                    className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                    disabled={loading}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>

            {/* Main Amount */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-md font-medium text-gray-800 dark:text-gray-200">Main Coordinator Amounts</h4>
                <button
                  type="button"
                  onClick={() => addCoordinatorAmount('mainAmount')}
                  className="flex items-center px-3 py-1 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                  disabled={loading}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Main Amount
                </button>
              </div>
              {formData.mainAmount?.map((amount, index) => (
                <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <input
                    type="text"
                    value={amount.title}
                    onChange={(e) => updateCoordinatorAmount('mainAmount', index, 'title', e.target.value)}
                    placeholder="Role title (e.g., Main Coordinator)"
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:text-white"
                    disabled={loading}
                  />
                  <input
                    type="number"
                    value={amount.amount}
                    onChange={(e) => updateCoordinatorAmount('mainAmount', index, 'amount', parseInt(e.target.value) || 0)}
                    placeholder="Amount"
                    min="0"
                    className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:text-white"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => removeCoordinatorAmount('mainAmount', index)}
                    className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                    disabled={loading}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>

            {/* Trainee Amount */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-md font-medium text-gray-800 dark:text-gray-200">Trainee Coordinator Amounts</h4>
                <button
                  type="button"
                  onClick={() => addCoordinatorAmount('traineeAmount')}
                  className="flex items-center px-3 py-1 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                  disabled={loading}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Trainee Amount
                </button>
              </div>
              {formData.traineeAmount?.map((amount, index) => (
                <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <input
                    type="text"
                    value={amount.title}
                    onChange={(e) => updateCoordinatorAmount('traineeAmount', index, 'title', e.target.value)}
                    placeholder="Role title (e.g., Trainee Coordinator)"
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:text-white"
                    disabled={loading}
                  />
                  <input
                    type="number"
                    value={amount.amount}
                    onChange={(e) => updateCoordinatorAmount('traineeAmount', index, 'amount', parseInt(e.target.value) || 0)}
                    placeholder="Amount"
                    min="0"
                    className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:text-white"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => removeCoordinatorAmount('traineeAmount', index)}
                    className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                    disabled={loading}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-4 shadow-lg z-10">
            <div className="max-w-4xl mx-auto flex items-center justify-end space-x-3">
              <button
                type="button"
                onClick={onCancel}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Updating...
                  </div>
                ) : (
                  'Update Inventory'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
